import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonComponent } from '../../button/button.component';
import { CustomerFrontendManagementService } from '../../../../core/customer-frontend-management.service';
import { KeycloakUserDetailResponse } from '../../../models/keycloak-user-management/keycloak-user-detail-response.model';
import { AddKeycloakUserPayload } from '../../../models/keycloak-user-management/add-keycloak-user-payload.model';
import { UpdateKeycloakUserPayload } from '../../../models/keycloak-user-management/update-keycloak-user-payload.model';

type DialogMode = 'add' | 'edit';

@Component({
  selector: 'app-keycloak-user-management-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, DropdownModule, InputTextModule, ButtonComponent],
  templateUrl: './keycloak-user-management-dialog.component.html',
  styleUrl: './keycloak-user-management-dialog.component.scss',
})
export class KeycloakUserManagementDialogComponent {
  readonly emailMaxLength = 255;
  readonly nameMaxLength = 255;
  readonly maxLengthReachedMessage = 'Die maximale Zeichenanzahl wurde erreicht.';
  readonly recoveryHint =
    'Bei diesem Kundennutzer ist ein Fehler aufgetreten. Sie können ihn neu speichern oder direkt löschen.';
  private readonly emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  @Output() addUser = new EventEmitter<AddKeycloakUserPayload>();
  @Output() updateUser = new EventEmitter<UpdateKeycloakUserPayload>();
  @Output() recreateUser = new EventEmitter<UpdateKeycloakUserPayload>();
  @Output() deactivateUser = new EventEmitter<string>();
  @Output() reactivateUser = new EventEmitter<string>();
  @Output() deleteUser = new EventEmitter<string>();
  @Output() deleteDatabaseUser = new EventEmitter<string>();
  @Input() allowDeactivate = true;

  visible = false;
  showDeleteConfirm = false;
  showReactivateConfirm = false;
  showPermanentDeleteConfirm = false;
  mode: DialogMode = 'add';

  editingUser: KeycloakUserDetailResponse | null = null;

  email = '';
  firstName = '';
  lastName = '';
  selectedCustomerId: string | null = null;
  customerOptions: { label: string; value: string }[] = [];
  loading = false;
  formErrorMessage = '';
  emailErrorMessage = '';
  firstNameErrorMessage = '';
  lastNameErrorMessage = '';

  constructor(private cfmService: CustomerFrontendManagementService) {}

  get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  get selectedCustomerLabel(): string {
    return this.customerOptions.find((option) => option.value === this.selectedCustomerId)?.label || this.selectedCustomerId || '-';
  }

  get isEditableUser(): boolean {
    return this.editingUser?.editable ?? true;
  }

  get isRecoveryMode(): boolean {
    return this.isEditMode && !this.isEditableUser;
  }

  get canSave(): boolean {
    if (this.hasValidationErrors()) {
      return false;
    }
    if (this.mode === 'add') {
      return this.email.trim().length > 0 && !!this.selectedCustomerId;
    }
    if (this.isRecoveryMode) {
      return !!this.selectedCustomerId && this.email.trim().length > 0;
    }
    return (
      !!this.selectedCustomerId &&
      (this.email.trim().length > 0 || this.firstName.trim().length > 0 || this.lastName.trim().length > 0)
    );
  }

  showAddDialog(): void {
    this.resetState();
    this.mode = 'add';
    this.visible = true;
    this.loadCustomers();
  }

  showEditDialog(user: KeycloakUserDetailResponse): void {
    this.resetState();
    this.mode = 'edit';
    this.editingUser = user;
    this.email = user.email;
    this.firstName = user.firstName || '';
    this.lastName = user.lastName || '';
    this.selectedCustomerId = user.customerId;
    this.visible = true;
    this.loadCustomers();
  }

  showDeleteDialog(user: KeycloakUserDetailResponse): void {
    this.resetState();
    this.mode = 'edit';
    this.editingUser = user;
    this.visible = true;
    this.showDeleteConfirm = true;
  }

  close(): void {
    this.visible = false;
  }

  save(): void {
    this.clearFormErrors();
    if (!this.validateForm() || !this.canSave) return;

    if (this.mode === 'add') {
      const payload: AddKeycloakUserPayload = {
        email: this.email.trim(),
        customerId: this.selectedCustomerId as string,
        ...(this.firstName.trim() && { firstName: this.firstName.trim() }),
        ...(this.lastName.trim() && { lastName: this.lastName.trim() }),
      };
      this.addUser.emit(payload);
    } else {
      if (!this.editingUser) return;
      const payload: UpdateKeycloakUserPayload = {
        id: this.editingUser.id,
        ...(this.selectedCustomerId && { customerId: this.selectedCustomerId }),
        ...(this.email.trim() && { updateEmail: this.email.trim() }),
        updateFirstName: this.firstName.trim(),
        updateLastName: this.lastName.trim(),
      };
      if (this.isRecoveryMode) {
        this.recreateUser.emit(payload);
      } else {
        this.updateUser.emit(payload);
      }
    }
  }

  onClickDelete(): void {
    this.showDeleteConfirm = true;
  }

  confirmDelete(): void {
    if (this.editingUser) {
      this.deactivateUser.emit(this.editingUser.id);
    }
    this.showDeleteConfirm = false;
    this.close();
  }

  cancelDelete(): void {
    this.showDeleteConfirm = false;
  }

  onClickReactivate(): void {
    this.showReactivateConfirm = true;
  }

  confirmReactivate(): void {
    if (this.editingUser) {
      this.reactivateUser.emit(this.editingUser.id);
    }
    this.showReactivateConfirm = false;
    this.close();
  }

  cancelReactivate(): void {
    this.showReactivateConfirm = false;
  }

  onClickPermanentDelete(): void {
    this.showPermanentDeleteConfirm = true;
  }

  confirmPermanentDelete(): void {
    if (this.editingUser) {
      if (this.isRecoveryMode) {
        this.deleteDatabaseUser.emit(this.editingUser.id);
      } else {
        this.deleteUser.emit(this.editingUser.id);
      }
    }
    this.showPermanentDeleteConfirm = false;
    this.close();
  }

  cancelPermanentDelete(): void {
    this.showPermanentDeleteConfirm = false;
  }

  showEmailConflict(message: string): void {
    this.emailErrorMessage = message;
    this.formErrorMessage = message;
  }

  showFormError(message: string): void {
    this.formErrorMessage = message;
  }

  clearFormErrors(): void {
    this.formErrorMessage = '';
    this.emailErrorMessage = '';
    this.firstNameErrorMessage = '';
    this.lastNameErrorMessage = '';
  }

  isEmailMaxLengthReached(): boolean {
    return this.email.length >= this.emailMaxLength;
  }

  isFirstNameMaxLengthReached(): boolean {
    return this.firstName.length >= this.nameMaxLength;
  }

  isLastNameMaxLengthReached(): boolean {
    return this.lastName.length >= this.nameMaxLength;
  }

  private loadCustomers(): void {
    this.loading = true;
    this.cfmService.getAllCustomers().subscribe({
      next: (customers) => {
        this.customerOptions = (customers || []).map((c) => ({ value: c.id, label: c.name }));
        this.loading = false;
      },
      error: () => {
        this.customerOptions = [];
        this.loading = false;
      },
    });
  }

  private resetState(): void {
    this.editingUser = null;
    this.email = '';
    this.firstName = '';
    this.lastName = '';
    this.selectedCustomerId = null;
    this.customerOptions = [];
    this.showDeleteConfirm = false;
    this.showReactivateConfirm = false;
    this.showPermanentDeleteConfirm = false;
    this.clearFormErrors();
  }

  private validateForm(): boolean {
    const trimmedEmail = this.email.trim();
    const trimmedFirstName = this.firstName.trim();
    const trimmedLastName = this.lastName.trim();

    if (trimmedEmail && !this.emailRegex.test(trimmedEmail)) {
      this.emailErrorMessage = 'Bitte geben Sie eine gueltige E-Mail-Adresse ein.';
    }

    if (trimmedFirstName.length > this.nameMaxLength) {
      this.firstNameErrorMessage = `Der Vorname darf maximal ${this.nameMaxLength} Zeichen haben.`;
    }

    if (trimmedLastName.length > this.nameMaxLength) {
      this.lastNameErrorMessage = `Der Nachname darf maximal ${this.nameMaxLength} Zeichen haben.`;
    }

    if (trimmedEmail.length > this.emailMaxLength) {
      this.emailErrorMessage = `Die E-Mail darf maximal ${this.emailMaxLength} Zeichen haben.`;
    }

    return !this.hasValidationErrors();
  }

  private hasValidationErrors(): boolean {
    return !!(this.emailErrorMessage || this.firstNameErrorMessage || this.lastNameErrorMessage);
  }
}
